import { useAuthor } from '@/hooks/useAuthor';
import { ARCHIVE_PUBKEY } from '@/hooks/useArchiveNotes';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { ArchiveBar, type ArchiveBarProgress } from '@/components/ArchiveBar';
import { ArchiveSeal } from '@/components/ArchiveSeal';
import { DEFAULT_SUMMARY, cleanAbout, isHiddenWebsite } from '@/lib/archiveProfile';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useEffect, useRef, useState } from 'react';
import { MorseDots } from '@/components/MorseDots';
import { subscribeToScroll, useHeroProgress } from '@/hooks/useScrollLinked';

/**
 * The jump link shown in the fixed bar. The Archive and the Directory are one
 * thing, so the two names read as a single link ("Archive & Directory") to the
 * start of the archive.
 */
const BAR_SECTIONS = [{ id: 'archive-feed', label: 'Archive', suffix: 'Directory' }] as const;

/** The archive key's low 64 bits, grouped in fours like a stamp serial number. */
const FILE_NO = ARCHIVE_PUBKEY.slice(-16).match(/.{4}/g)?.join(' ') ?? '';
const SEAL_IMPRINT = `PUBLIC RECORD · FILE NO. ${FILE_NO}`;

interface ArchiveHeaderProps {
  /** True while relays are still being scanned, even after the first results. */
  scanning?: boolean;
  /** Reading progress through the feed, shown in the compact bar. */
  progress?: ArchiveBarProgress;
  /** Total records in the archive; shown in place of the dots once the scan is done. */
  recordCount?: number;
}

export function ArchiveHeader({ scanning = false, progress, recordCount }: ArchiveHeaderProps) {
  const author = useAuthor(ARCHIVE_PUBKEY);
  const metadata = author.data?.metadata;
  const npub = nip19.npubEncode(ARCHIVE_PUBKEY);
  const isMobile = useIsMobile();

  const about = metadata?.about ? cleanAbout(metadata.about) : '';
  const website = metadata?.website && !isHiddenWebsite(metadata.website)
    ? metadata.website
    : undefined;

  const title = metadata?.display_name ?? metadata?.name ?? 'Anonymous Archive';
  const summary = about || DEFAULT_SUMMARY;

  // The hero's scroll-linked motion: --hero-p (0 at the top, 1 once it has
  // scrolled away) is written onto the section, and the CSS does the rest.
  const heroRef = useRef<HTMLElement | null>(null);
  useHeroProgress(heroRef);

  // The compact bar appears once the hero has mostly scrolled away. This one
  // does need React (it mounts a control), so it flips a boolean, not a number.
  const [showBar, setShowBar] = useState(false);
  useEffect(
    () =>
      subscribeToScroll(() => setShowBar(window.scrollY > window.innerHeight * 0.7)),
    [],
  );

  const scrollToId = (id: string) => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <header className="bg-background">
      {/* ── Compact bar — the seal and title settle into a slim fixed header
          once the hero has scrolled away. Hairline, no shadow. ───────────── */}
      <ArchiveBar
        position="fixed"
        visible={showBar}
        progress={progress}
        sections={BAR_SECTIONS}
        progressSection="archive-feed"
        scanning={scanning}
        showContact
      />

      {/* ── Scene 1 — the title card. A calm, full-height hero: seal, nameplate,
          subtitle, and a brief summary, with Enter at its foot. ───────────── */}
      <section
        ref={heroRef}
        // Exactly one screen, so the first view is the hero and nothing else:
        // the section below ("Browse the record" and the record count) begins
        // on the next screen. There is no rule between them, so it is still one
        // continuous scroll.
        className="relative flex min-h-[100svh] flex-col overflow-x-clip text-center"
      >
        <div className="relative mx-auto flex w-full max-w-3xl flex-1 flex-col items-center px-4 pb-8 pt-8 md:pt-6">
          {/* ── The seal — the dominant element, scaled to the screen height so
              it nearly fills the first view. A hairline halo and a deep, soft
              shadow lift it off the paper. ── */}
          <Link
            to={`/${npub}`}
            // A rubber stamp pressed into the paper: no ring around it, no drop
            // shadow, a hair off-level, and multiplied into the page so the paper
            // shows through the ink instead of the seal sitting on top like a
            // sticker.
            // As the reader leaves the hero the seal shrinks, lifts and fades
            // (see .hero-seal in index.css), on phones as well as desktop. It is
            // driven by a CSS variable rather than React state, so it stays
            // smooth, and the progress is clamped so the address bar collapsing
            // on a phone can't make it jump.
            className="hero-seal inline-block rounded-full mix-blend-multiply"
            aria-label={`Archive profile — ${SEAL_IMPRINT}`}
          >
            <ArchiveSeal
              picture={metadata?.picture}
              name={metadata?.name}
              loading={author.isLoading}
              // Desktop: the seal takes whatever height is left once the title,
              // text and Enter are accounted for (~380px of fixed content plus
              // the title's share of the screen), so the seal stays the focal
              // point. Capped at 700px, floored at 240px.
              size={
                isMobile
                  ? 'min(80vw, 44svh, max(120px, calc(100svh - 490px)))'
                  : 'min(82vw, 700px, max(240px, calc(100svh - 385px)))'
              }
              borderWidth={0}
              imprint={SEAL_IMPRINT}
              className="mx-auto rounded-full"
            />
          </Link>

          {/* Everything under the seal drifts up and fades as one group (see
              .hero-copy in index.css). */}
          <div className="hero-copy flex w-full flex-col items-center">
          {author.isLoading ? (
            <Skeleton className="h-14 w-72 mx-auto mt-5" />
          ) : (
            <Link to={`/${npub}`}>
              <h1 className="mt-5 font-masthead text-[clamp(3.25rem,14vw,7.5rem)] md:text-[clamp(3rem,min(10vw,11svh),7rem)] leading-[0.9] tracking-tight text-foreground [-webkit-text-stroke:1.5px_var(--foreground)]">
                {title}
              </h1>
            </Link>
          )}

          {/* Dateline: refined engraved caps with hairline rule */}
          <div className="mt-3 flex w-full max-w-lg items-center gap-3">
            <span aria-hidden="true" className="h-px flex-1 bg-foreground/25" />
            <p className="font-engraved text-xs font-semibold uppercase tracking-[0.3em] text-foreground/80 md:text-sm">
              Archive &amp; Directory
            </p>
            <span aria-hidden="true" className="h-px flex-1 bg-foreground/25" />
          </div>

          {/* ── Purpose: a short statement, the stewardship credential, and
              the litigation line. ── */}
          {/* Always shown, even on a short screen. */}
          <p className="mt-3.5 max-w-md text-sm leading-relaxed text-foreground/80 whitespace-pre-line line-clamp-4 md:line-clamp-3 md:text-base">
            {summary}
          </p>

          {/* Clean, authoritative mission statement in engraved caps with restrained flag colors */}
          <p className="mt-3 font-engraved text-xs font-bold uppercase tracking-[0.2em] text-foreground">
            Built for{' '}
            <span className="text-flag-red">
              Preservation
            </span>
            {', '}
            <span>
              Education
            </span>{' '}
            &amp;{' '}
            <span className="text-flag-navy">
              Litigation
            </span>
          </p>

          {/* The stewardship credential: clean, official institutional imprint */}
          <p className="mt-2.5 flex flex-wrap items-center justify-center gap-1.5 text-center">
            <span className="font-engraved text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
              Managed by
            </span>
            <span className="font-display text-sm font-semibold text-foreground md:text-base">
              9/11 victim family members &amp; survivors
            </span>
          </p>

          {website && (
            <a
              href={website}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-xs text-primary underline hover:opacity-70 transition-opacity"
            >
              {website}
            </a>
          )}

          {/* The entry action; the file number lives on the seal above. */}
          <div className="mt-5 flex flex-col items-center">
            <button
              type="button"
              onClick={() => scrollToId('archive-feed')}
              className="group inline-flex h-11 items-center justify-center gap-2 rounded-full bg-foreground px-7 font-engraved text-[12px] font-bold uppercase tracking-[0.2em] text-background shadow-[0_4px_12px_-4px_rgba(40,25,10,0.45)] transition-[transform,box-shadow] duration-150 ease-out [-webkit-tap-highlight-color:transparent] hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Browse the archive
            </button>

            {/* Three morse dots under the button. The archive starts loading the
                moment the page opens, and these show it: they blink while the
                scan runs, then rest as steady dots once it has finished. */}
            {!scanning && recordCount ? (
              <p className="mt-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] tabular-nums text-foreground/70 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-500">
                {recordCount.toLocaleString()} records
              </p>
            ) : (
              <MorseDots
                active={scanning}
                className="mt-4 gap-1.5 text-foreground/70 [&>span]:h-1.5 [&>span]:w-1.5"
              />
            )}
            <span className="sr-only" role="status">
              {scanning
                ? 'Loading the archive'
                : recordCount
                  ? `Archive loaded: ${recordCount.toLocaleString()} records`
                  : 'Archive loaded'}
            </span>
          </div>
          </div>
        </div>
      </section>

    </header>
  );
}
